import { useEffect, useMemo, useState } from 'react';
import { useAuth } from 'auth-sso-kit/client/index.js';
import { api } from '../../api/client';
import type { Personne } from '../../types';

// Une seule requête par compte et par chargement de page, partagée par tous les composants.
const cache = new Map<number, Promise<Personne | null>>();
// Les réponses déjà reçues : une page rouverte ne repasse pas par « chargement ».
const recues = new Map<number, Personne | null>();
function chargerMoi(userId: number): Promise<Personne | null> {
  let p = cache.get(userId);
  if (!p) {
    p = api.getMaPersonne().then((r) => { recues.set(userId, r); return r; }).catch(() => { cache.delete(userId); return null; });
    cache.set(userId, p);
  }
  return p;
}

/**
 * Fiche personne du compte connecté. Le SERVEUR est la référence
 * (`GET /personnes/moi` : users.personne_id, sinon appariement par e-mail) : le
 * kit d'authentification n'expose pas `personne_id`, donc une fiche sans e-mail
 * correspondant au compte n'était pas reconnue côté écran (demande impossible
 * à créer, calendrier vide). La liste fournie par l'appelant sert à renvoyer
 * l'objet déjà chargé ; le repli par e-mail ne couvre que le temps du chargement.
 * `chargement` : la réponse du serveur n'est pas encore arrivée — « ce compte n'est relié à aucune fiche » ne doit pas encore se dire.
 */
export function useMaPersonneEtat(personnes: Personne[]): { personne: Personne | null; chargement: boolean } {
  const { user } = useAuth();
  const userId = (user as { id?: number } | null)?.id ?? null;
  const [moi, setMoi] = useState<Personne | null>(() => (userId != null ? recues.get(userId) ?? null : null));
  const [fini, setFini] = useState(() => userId == null || recues.has(userId));
  useEffect(() => {
    if (userId == null) { setMoi(null); setFini(true); return; }
    let annule = false;
    chargerMoi(userId).then((p) => { if (!annule) { setMoi(p); setFini(true); } });
    return () => { annule = true; };
  }, [userId]);
  const personne = useMemo(() => {
    if (!user) return null;
    if (moi) return personnes.find((p) => p.id === moi.id) ?? moi;
    // Repli le temps du chargement (ancien appariement : compte lié, puis e-mail).
    if (personnes.length === 0) return null;
    const parId = (user as { personne_id?: number | null }).personne_id;
    if (parId != null) return personnes.find((p) => p.id === parId) ?? null;
    if (!user.email) return null;
    return personnes.find((p) => p.email && p.email.toLowerCase() === user.email.toLowerCase()) ?? null;
  }, [user, moi, personnes]);
  return { personne, chargement: !fini };
}

export function useMaPersonne(personnes: Personne[]): Personne | null {
  return useMaPersonneEtat(personnes).personne;
}
